import { useMemo, useState } from 'react';
import { ChevronDown, ChevronUp, ScanSearch, Circle, Link2Off, Layers } from 'lucide-react';
import { cn } from '@/lib/utils';
import type { AtlasGap } from '@/api/knowledgeAtlas';

/**
 * A quiet, collapsible "Coverage" panel that surfaces what the corpus is
 * MISSING — the knowledge-gap counterpart to the connections legend. It groups
 * gaps by kind (Isolated / Weakly linked / Shallow coverage), each row showing a
 * title + detail; clicking a row asks the parent to fly to / select the subject
 * document or district. Light, unobtrusive, planning-styled — informative, not
 * alarming.
 */

interface CoveragePanelProps {
  gaps: AtlasGap[];
  /** Click a gap subject -> fly to / select it on the map. */
  onSelectDocument: (documentId: number) => void;
  onSelectDistrict: (districtId: number) => void;
}

const COVERAGE = {
  maxRowsPerGroup: 5, // keep each group a strip, not a wall
} as const;

// The three gap kinds, in display order, with their grouping metadata.
const GROUPS: {
  kind: AtlasGap['kind'];
  label: string;
  icon: typeof Circle;
  blurb: string;
}[] = [
  {
    kind: 'isolated',
    label: 'Isolated',
    icon: Link2Off,
    blurb: 'Disconnected from the rest of the map',
  },
  {
    kind: 'underconnected',
    label: 'Weakly linked',
    icon: Circle,
    blurb: 'Present, but thinly wired in',
  },
  {
    kind: 'thin',
    label: 'Shallow coverage',
    icon: Layers,
    blurb: 'Districts with little depth',
  },
];

export function CoveragePanel({
  gaps,
  onSelectDocument,
  onSelectDistrict,
}: CoveragePanelProps) {
  const [open, setOpen] = useState(false);

  const grouped = useMemo(() => {
    const m = new Map<AtlasGap['kind'], AtlasGap[]>();
    for (const g of gaps) {
      if (!m.has(g.kind)) m.set(g.kind, []);
      m.get(g.kind)!.push(g);
    }
    // Sort each group by severity (most notable first).
    for (const list of m.values()) list.sort((a, b) => b.severity - a.severity);
    return m;
  }, [gaps]);

  if (gaps.length === 0) return null;

  // Headline stat, e.g. "3 isolated · 2 shallow districts".
  const isolatedCount = grouped.get('isolated')?.length ?? 0;
  const thinCount = grouped.get('thin')?.length ?? 0;
  const weakCount = grouped.get('underconnected')?.length ?? 0;
  const statParts: string[] = [];
  if (isolatedCount > 0) statParts.push(`${isolatedCount} isolated`);
  if (weakCount > 0) statParts.push(`${weakCount} weakly linked`);
  if (thinCount > 0)
    statParts.push(`${thinCount} shallow district${thinCount === 1 ? '' : 's'}`);
  const stat = statParts.join(' · ');

  const selectGap = (g: AtlasGap) => {
    if (g.documentId != null) onSelectDocument(g.documentId);
    else if (g.districtId != null) onSelectDistrict(g.districtId);
  };

  return (
    <div
      className="absolute bottom-4 right-4 z-20 w-[300px] overflow-hidden rounded-lg border border-slate-200 bg-white/92 backdrop-blur"
      style={{ boxShadow: '0 10px 30px -18px rgba(15,23,42,0.28)' }}
    >
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        className="flex w-full items-center gap-2 px-3 py-2 text-left transition-colors hover:bg-slate-50"
      >
        <ScanSearch className="h-3.5 w-3.5 shrink-0 text-slate-400" />
        <span className="text-[10px] font-medium uppercase tracking-[0.16em] text-slate-500">
          Coverage
        </span>
        {stat && (
          <span className="ml-2 truncate text-[11px] font-normal normal-case tracking-normal text-slate-400">
            {stat}
          </span>
        )}
        <span className="ml-auto shrink-0 text-slate-400">
          {open ? <ChevronDown className="h-3.5 w-3.5" /> : <ChevronUp className="h-3.5 w-3.5" />}
        </span>
      </button>

      {open && (
        <div className="max-h-[44vh] overflow-y-auto border-t border-slate-100 px-1.5 py-1.5">
          {GROUPS.map((group) => {
            const list = grouped.get(group.kind);
            if (!list || list.length === 0) return null;
            const Icon = group.icon;
            const rows = list.slice(0, COVERAGE.maxRowsPerGroup);
            const overflow = list.length - rows.length;
            return (
              <div key={group.kind} className="mb-1.5 last:mb-0">
                <div className="flex items-center gap-1.5 px-2 py-1">
                  <Icon className="h-3 w-3 shrink-0 text-slate-400" />
                  <span className="text-[10px] font-medium uppercase tracking-[0.16em] text-slate-500">
                    {group.label}
                  </span>
                  <span className="ml-auto text-[10px] tabular-nums text-slate-400">
                    {list.length}
                  </span>
                </div>
                <ul>
                  {rows.map((g, i) => {
                    const clickable = g.documentId != null || g.districtId != null;
                    return (
                      <li key={`${group.kind}-${i}`}>
                        <button
                          type="button"
                          disabled={!clickable}
                          onClick={() => selectGap(g)}
                          className={cn(
                            'w-full rounded-md px-2 py-1.5 text-left transition-colors',
                            clickable ? 'hover:bg-slate-100' : 'cursor-default'
                          )}
                        >
                          <div className="truncate text-[12px] font-medium text-slate-700">
                            {g.title}
                          </div>
                          {g.detail && (
                            <div className="mt-0.5 text-[11px] leading-snug text-slate-500">
                              {g.detail}
                            </div>
                          )}
                        </button>
                      </li>
                    );
                  })}
                </ul>
                {overflow > 0 && (
                  <div className="px-2 pb-1 pt-0.5 text-[11px] text-slate-400">
                    +{overflow} more
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
